import { notifications } from '@mantine/notifications';
import { observer } from 'mobx-react-lite';
import * as React from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import SearchTreeItem from '@/components/shared/searchTree/SearchTreeItem';
import BaseClickable from '@/components/ui/BaseClickable';
import { ROUTES } from '@/configs/routes';
import { getDocumentLockMessage } from '@/entities/Document/documentContentLock';
import { DocumentSearchItem } from '@/entities/DocumentsSearch';
import { useSidebarDocumentsStore } from '@/stores/local/sidebar/SidebarDocumentsStore';

import Actions from '../Actions';

import s from './SearchItem.module.scss';

type Props = {
  item: DocumentSearchItem;
  withSelection?: boolean;
};

const SearchItem: React.FC<Props> = ({ item, withSelection = false }) => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { searchDocumentStore } = useSidebarDocumentsStore();

  const handleClick = React.useCallback(() => {
    if (!item.contentAvailable) {
      notifications.show({
        color: 'yellow',
        title: 'Документ недоступен',
        message: getDocumentLockMessage(item.availabilityIconReason),
        autoClose: 6000,
      });

      return;
    }

    navigate(
      ROUTES.organization.documents.document.documentPreview.create(id ?? '', item.id, String(item.pageNumber)),
      {
        state: { name: item.name },
      },
    );
  }, [navigate, id, item.availabilityIconReason, item.contentAvailable, item.id, item.name, item.pageNumber]);

  const handlePathClick = React.useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      searchDocumentStore.reset();
      navigate(ROUTES.organization.documents.document.aiSummary.create(id ?? '', item.id), {
        state: {
          documentsFolderPath: item.path.map((p) => p.id),
        },
      });
    },
    [searchDocumentStore, navigate, id, item.id, item.path],
  );

  return (
    <BaseClickable className={s.root} onClick={handleClick}>
      <SearchTreeItem
        item={item}
        onPathClick={handlePathClick}
        actions={
          <Actions
            documentId={item.id}
            searchResultId={item.searchResultId}
            contentAvailable={item.contentAvailable}
            availabilityBlockReason={item.availabilityIconReason}
            withSelection={withSelection}
          />
        }
      />
    </BaseClickable>
  );
};

export default observer(SearchItem);
